import {AfterViewInit, Component, OnDestroy, ViewChild} from "@angular/core";
import {combineLatest, of, Subject, tap} from "rxjs";
import {MatSort} from "@angular/material/sort";
import {MatTableDataSource} from "@angular/material/table";
import {MatPaginator} from "@angular/material/paginator";
import {FormControl} from "@angular/forms";
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    map,
    startWith,
    switchMap,
    takeUntil,
} from "rxjs/operators";
import {MatDialog} from "@angular/material/dialog";
import {TranslateService} from "@ngx-translate/core";
import {UserModel} from "../../../../models/user.model";
import {NotificationService} from "../../../shared/services/notification.service";
import {PaginatedTypedListResponse} from "../../../../interfaces/responses";
import {UsersService} from "../../../shared/services/users.service";
import {UserModalComponent} from "./add-user-modal/user-modal.component";
import {SiteModel} from "../../../../models/site.model";
import {UnityModel} from "../../../../models/unity.model";
import {ROLE_OPTIONS, UserRole} from "../../../../enums/user-role.enum";
import {ConfirmDeleteModalComponent} from "./confirm-delete-modal/confirm-delete-modal.component";
import {UserSiteModel} from "../../../../models/user-site.model";
import {PaginationComponent} from "../../../shared/components/pagination/pagination.component";

@Component({
    selector: "app-users-management",
    templateUrl: "./users-management.component.html",
    styleUrls: ["./users-management.component.scss"],
})
export class UsersManagementComponent implements OnDestroy, AfterViewInit {
    @ViewChild(PaginationComponent, {static: true}) paginator:
        | PaginationComponent
        | undefined;
    @ViewChild(MatSort) private sort!: MatSort;
    protected readonly ROLE_OPTIONS = ROLE_OPTIONS;

    private destroy$ = new Subject<void>();

    displayedColumns: string[] = [
        "igg",
        "first_name",
        "last_name",
        "email",
        "sites",
        "unities",
        "role",
        "actions",
    ];
    totalItems = 0;
    pageIndex = 1;
    pageSize = 10;

    dataSource = new MatTableDataSource<UserModel>();

    searchControl = new FormControl("");
    roleFilterControl = new FormControl("");

    constructor(
        private notificationService: NotificationService,
        private usersService: UsersService,
        private dialog: MatDialog,
        private translate: TranslateService,
    ) {
    }

    ngAfterViewInit() {
        const sort$ = this.sort.sortChange.pipe(
            takeUntil(this.destroy$),
            debounceTime(200),
            tap(() => (this.pageIndex = 1)),
            startWith({active: this.sort.active, direction: this.sort.direction}),
        );


        const search$ = this.searchControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            debounceTime(300),
            distinctUntilChanged(),
            startWith(this.searchControl.value || ""),
        );

        const roleFilter$ = this.roleFilterControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            debounceTime(300),
            distinctUntilChanged(),
            startWith(this.roleFilterControl.value || ""),
        );

        combineLatest([sort$, search$, roleFilter$])
            .pipe(
                switchMap(([sort, search, roleFilter]) =>
                    this.loadUsers(
                        this.pageIndex ? this.pageIndex : 1,
                        this.pageSize,
                        sort.active || "user.id",
                        sort.direction || "asc",
                        search || "",
                        roleFilter || "",
                    ),
                ),
                map((response) => this.processResponse(response)),
                takeUntil(this.destroy$),
            )
            .subscribe((data) => {
                this.dataSource.data = data;
            });
    }

    loadUsers(
        page: number,
        perPage: number,
        sortField: string,
        sortOrder: string,
        search: string,
        roleFilter: string,
    ) {
        return this.usersService.getUsers(page, perPage, sortField, sortOrder, search, roleFilter).pipe(
            takeUntil(this.destroy$),
            catchError((error) => {
                this.notificationService.error(
                    this.translate.instant("pages.users_management.load_error"),
                );
                return of({
                    status: 500,
                    message: "An error occurred while loading administrators.",
                    data: {
                        data: [],
                        total: 0,
                        current_page: 1,
                        per_page: 10,
                    },
                } as PaginatedTypedListResponse<UserModel>);
            }),
        );
    }

    private processResponse(
        response: PaginatedTypedListResponse<UserModel>,
    ): UserModel[] {
        this.totalItems = response.data.total;
        return response.data.data;
    }

    refreshData() {
        // On part de this.pageIndex et this.pageSize
        const pageIndex = this.pageIndex;   // déjà géré par votre custom paginator
        const pageSize = this.pageSize;
        const sortField = this.sort.active || 'user.id';
        const sortOrder = this.sort.direction || 'ASC';
        const search = this.searchControl.value || '';
        const roleFilter = this.roleFilterControl.value || '';

        this.loadUsers(pageIndex, pageSize, sortField, sortOrder, search, roleFilter).subscribe(response => {
            this.dataSource.data = this.processResponse(response);
        });
    }


    openUserDialog(user?: UserModel) {
        const dialogRef = this.dialog.open(UserModalComponent, {
            width: '800px', // Augmenté pour un meilleur affichage
            height: 'auto',
            maxHeight: '90vh',
            disableClose: true,
            data: {user},
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result) {
                    this.refreshData();
                }
            });
    }

    deleteUser(user: UserModel) {
        const dialogRef = this.dialog.open(ConfirmDeleteModalComponent, {
            width: '400px',
            data: {
                firstName: user.firstName,
                lastName: user.lastName
            },
            disableClose: true
        });

        dialogRef.afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe(result => {
                if (result && user.id) {
                    this.usersService.delete(user.id)
                        .pipe(takeUntil(this.destroy$))
                        .subscribe({
                            next: () => {
                                this.notificationService.success(
                                    this.translate.instant('pages.users_management.delete_success')
                                );
                                this.refreshData();
                            },
                            error: () => {
                                this.notificationService.error(
                                    this.translate.instant('messages.error.generic')
                                );
                            }
                        });
                }
            });
    }

    getLabels(items: SiteModel[] | UnityModel[]): string {
        return items.map(item => item.label).join(', ');
    }

    getUserSiteLabels(userSites: UserSiteModel[]): string {
        const sites = userSites.map(item => item.site) || [];
        return sites.map(item => item.label).join(', ');
    }

    getButtonClass(role: UserRole): string {
        switch (role) {
            case UserRole.CONSULTANT_HQ:
                return 'consultant-hq';
            case UserRole.CHIEF_INSPECTOR:
                return 'chief-inspector';
            case UserRole.INSPECTOR:
                return 'inspector';
            case UserRole.SUPER_ADMIN:
                return 'super-admin';
            default:
                return '';
        }
    }

    getRoleLabel(role: UserRole): string {
        const roleOption = ROLE_OPTIONS.find(option => option.value === role);
        return roleOption ? roleOption.label : '';
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onPaginate(state: { page: number; pageOffset: number }) {
        this.pageIndex = state.page;
        this.pageSize = state.pageOffset;
        this.refreshData();
    }
}
